import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { GlossaryEntry } from './types';
import { glossaryStorage } from './storage';
import { GlossaryForm } from './GlossaryForm';
import { AlphabetNav } from './AlphabetNav';
import { GlossarySection } from './GlossarySection';

export const Glossary: React.FC = () => {
  const [entries, setEntries] = useState<GlossaryEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<GlossaryEntry | undefined>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeLetter, setActiveLetter] = useState('All');

  useEffect(() => {
    const storedEntries = glossaryStorage.getEntries();
    // Sort entries alphabetically by acronym
    const sortedEntries = [...storedEntries].sort((a, b) => 
      a.acronym.localeCompare(b.acronym)
    );
    setEntries(sortedEntries);
  }, []);

  const handleAddEntry = () => {
    setSelectedEntry(undefined);
    setIsFormOpen(true);
  };

  const handleEditEntry = (entry: GlossaryEntry) => {
    setSelectedEntry(entry);
    setIsFormOpen(true);
  };

  const handleDeleteEntry = (id: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      glossaryStorage.deleteEntry(id);
      setEntries(glossaryStorage.getEntries());
    }
  };

  const handleSaveEntry = (entry: GlossaryEntry) => {
    if (selectedEntry) {
      glossaryStorage.updateEntry(entry);
    } else {
      glossaryStorage.addEntry(entry);
    }
    const updatedEntries = glossaryStorage.getEntries();
    // Sort entries alphabetically by acronym
    const sortedEntries = [...updatedEntries].sort((a, b) => 
      a.acronym.localeCompare(b.acronym)
    );
    setEntries(sortedEntries);
    setIsFormOpen(false);
    setSelectedEntry(undefined);
  };

  const handleLetterClick = (letter: string) => {
    setActiveLetter(letter);
    if (letter !== 'All') {
      const element = document.getElementById(`section-${letter}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Filter entries based on search query
  const filteredEntries = entries.filter(entry =>
    entry.acronym.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.meaning.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.explanation.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get all available letters from entries
  const availableLetters = new Set(
    filteredEntries.map(entry => entry.acronym[0].toUpperCase())
  );

  // Group entries by first letter
  const groupedEntries = filteredEntries.reduce((acc, entry) => {
    const letter = entry.acronym[0].toUpperCase();
    if (!acc[letter]) {
      acc[letter] = [];
    }
    acc[letter].push(entry);
    return acc;
  }, {} as Record<string, GlossaryEntry[]>);

  // Get sorted letters for display
  const sortedLetters = Array.from(new Set('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''))).sort();

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}
        >
          <Box component="h1" sx={{ typography: 'h6', m: 0 }}>
            Educational Acronyms Glossary
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
          >
            Add Entry
          </Button>
        </Box>

        <TextField
          fullWidth
          placeholder="Search glossary..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />

        <AlphabetNav
          activeLetter={activeLetter}
          availableLetters={availableLetters}
          onLetterClick={handleLetterClick}
        />

        <Divider />

        <Box 
          sx={{ 
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto',
            scrollBehavior: 'smooth',
            mx: -3,
            px: 3
          }}
        >
          {(activeLetter === 'All' ? sortedLetters : [activeLetter]).map(letter => (
            <GlossarySection
              key={letter}
              letter={letter}
              entries={groupedEntries[letter] || []}
              onEdit={handleEditEntry}
              onDelete={handleDeleteEntry}
            />
          ))}
          {filteredEntries.length === 0 && (
            <Box 
              sx={{ 
                py: 4, 
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              No entries found
            </Box>
          )}
        </Box>
      </Stack>

      <GlossaryForm
        open={isFormOpen}
        entry={selectedEntry}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedEntry(undefined);
        }}
        onSave={handleSaveEntry}
      />
    </Paper>
  );
};